/**
 * 个人资料。走后端 profiles.get / profiles.update（均要求已登录）。
 *
 * 后端 normalizeProfile 只认这四个字段，多传的会被丢弃：
 *   nickname / phone / email / avatarUrl
 *
 * 注意：这里的 phone、email 是用户自愿填写的联系方式，与登录账号是两回事
 * （登录账号在 users 表里是哈希存储的）。隐私政策把它们列为「可能收集的信息」，
 * 所以表单上要说清用途，不要默认必填。
 */
import { useEffect, useState } from "react";
import { callApi, getSession, setSession } from "../api";
import { site } from "../data/site";

type Profile = {
  nickname?: string;
  phone?: string;
  email?: string;
  avatarUrl?: string;
  updatedAt?: string;
};

export function ProfilePage({ onNavigate }: { onNavigate: (route: string) => void }) {
  const [profile, setProfile] = useState<Profile>({});
  const [state, setState] = useState<"anonymous" | "loading" | "ready" | "error">("loading");
  const [message, setMessage] = useState("");
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!getSession()) {
      setState("anonymous");
      return;
    }
    callApi<{ profile?: Profile | null }>("profiles.get").then((result) => {
      if (result.ok) {
        setProfile(result.profile ?? {});
        setState("ready");
      } else {
        setMessage(result.error);
        setState("error");
      }
    });
  }, []);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    const result = await callApi<{ profile?: Profile }>("profiles.update", { profile });
    setBusy(false);
    setFailed(!result.ok);
    if (result.ok) {
      if (result.profile) setProfile(result.profile);
      setMessage("已保存。");
    } else {
      setMessage(result.error);
    }
  }

  async function signOut() {
    await callApi("auth.logout");
    setSession(null);
    onNavigate("home");
  }

  if (state === "anonymous") {
    return (
      <main className="page profile-page">
        <h1>个人资料</h1>
        <p className="empty-state">
          请先
          <button type="button" onClick={() => onNavigate("auth")}>
            登录
          </button>
          。
        </p>
      </main>
    );
  }

  if (state === "loading") {
    return (
      <main className="page profile-page">
        <h1>个人资料</h1>
        <p className="empty-state">加载中…</p>
      </main>
    );
  }

  return (
    <main className="page profile-page">
      <h1>个人资料</h1>
      <p className="page-summary">这些信息仅用于账号识别与客服联系，可随时修改或留空。</p>

      <form className="auth-form" onSubmit={save}>
        <label>
          昵称
          <input
            type="text"
            value={profile.nickname ?? ""}
            maxLength={80}
            placeholder="留空则显示为「体验用户」"
            onChange={(e) => setProfile({ ...profile, nickname: e.target.value })}
          />
        </label>
        <label>
          联系手机号（选填）
          <input
            type="tel"
            value={profile.phone ?? ""}
            maxLength={32}
            placeholder="仅用于客服联系，可留空"
            onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
          />
        </label>
        <label>
          联系邮箱（选填）
          <input
            type="email"
            value={profile.email ?? ""}
            maxLength={120}
            placeholder="仅用于客服联系，可留空"
            onChange={(e) => setProfile({ ...profile, email: e.target.value })}
          />
        </label>
        <label>
          头像地址（选填）
          <input
            type="url"
            value={profile.avatarUrl ?? ""}
            maxLength={500}
            placeholder="https://…"
            onChange={(e) => setProfile({ ...profile, avatarUrl: e.target.value })}
          />
        </label>
        <button type="submit" disabled={busy}>
          {busy ? "保存中…" : "保存"}
        </button>
      </form>

      {message && <p className={failed ? "form-error" : "save-ok"}>{message}</p>}
      {profile.updatedAt && (
        <p className="auth-hint">最后更新：{profile.updatedAt.slice(0, 19).replace("T", " ")}</p>
      )}

      <section className="profile-rights">
        <h2>数据与账号</h2>
        <p>
          如需导出、删除个人数据或注销账号，请通过 {site.email} 联系我们，
          我们会在核验身份后处理。
        </p>
        <button type="button" onClick={signOut}>
          退出登录
        </button>
      </section>
    </main>
  );
}
